/** German speech-to-text via the browser's SpeechRecognition — $0, no
 * audio ever reaches our servers (only the recognized TEXT is sent).
 * Chrome/Edge ship it; where absent the speaking partner falls back to
 * typed input. A paid-STT upgrade (e.g. Azure Speech) swaps THIS module
 * only — the session API is text-based either way.
 */

export interface Recognizer {
  start(): void;
  stop(): void;
}

export interface RecognizerOptions {
  lang?: string;
  /** One finished utterance (the browser's VAD decided the learner is
   * done speaking). */
  onFinal: (text: string) => void;
  /** Recognition stopped (after a result, silence, or an error). */
  onEnd?: () => void;
  onError?: (error: unknown) => void;
}

type SpeechRecognitionCtor = new () => {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((event: { results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }> }) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: unknown) => void) | null;
  start(): void;
  stop(): void;
};

function recognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function sttAvailable(): boolean {
  return recognitionCtor() !== null;
}

/** Build a one-utterance recognizer (the caller re-starts it for the
 * continuous-conversation loop). Returns null where unsupported. */
export function createRecognizer(opts: RecognizerOptions): Recognizer | null {
  const Ctor = recognitionCtor();
  if (!Ctor) return null;
  const recognition = new Ctor();
  recognition.lang = opts.lang ?? "de-DE";
  recognition.continuous = false;
  recognition.interimResults = false;
  recognition.onresult = (event) => {
    let text = "";
    for (let i = 0; i < event.results.length; i += 1) {
      const result = event.results[i];
      if (result.isFinal) text += result[0].transcript;
    }
    if (text.trim()) opts.onFinal(text.trim());
  };
  recognition.onend = () => opts.onEnd?.();
  recognition.onerror = (event) => opts.onError?.(event);
  return {
    start: () => {
      try {
        recognition.start();
      } catch {
        // Already started — the browser throws; harmless.
      }
    },
    stop: () => recognition.stop(),
  };
}
